import { setCursor } from '../../lib/cursor';
import {
  addElement,
  defaultGenericElement,
  type GenericElement,
  updateElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { isInsideTransformHandle } from '../../lib/selection';
import { rescalePoints } from '../../lib/shapes';
import { moveMultipleElements } from '../../lib/transformation';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { updateEditorStore } from '../../stores/useEditorStore';
import { fillCircle } from '../../utils/canvas';
import { distance, type Point, snapToAngle } from '../../utils/math';
import { general } from '../general';
import { poly, type GenericPolyElement } from '../poly';

import { getLineBounds } from './utils/bounds';
import { getLineShape } from './utils/shape';
import { getLineTransformHandles } from './utils/transform';
import { translatePoint } from './utils/translation';

export interface LineElement extends GenericPolyElement {
  points: [Point, Point];
  strokeColor: string;
  strokeWidth: number;
  type: 'line';
}

const SNAPPING_ANGLE = 15;

const lineState = {
  newStrokeColor: '#3c87f7',
  newStrokeWidth: 4,
};

const lineActions = {
  activate: () => {
    setCursor(plugin.cursor);
    general.actions.activate(plugin.name);
  },
  setStrokeColor: (color: string) => {
    lineState.newStrokeColor = color;
  },
  setStrokeWidth: (width: number) => {
    lineState.newStrokeWidth = width;
  },
};

export type LinePlugin = Plugin<
  LineElement,
  typeof lineActions,
  typeof lineState
>;

const plugin: LinePlugin = {
  name: 'line',
  cursor: 'crosshair',
  actions: lineActions,
  state: lineState,
  events: {
    onPointerDown: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;

      if (button === 'left' && runtimeState.activeTool === 'line') {
        const newElement: LineElement = {
          ...defaultGenericElement(),
          originX: pos.x,
          originY: pos.y,
          points: [
            [0, 0],
            [0, 0],
          ],
          path: new Path2D(),
          strokeColor: lineState.newStrokeColor,
          strokeWidth: lineState.newStrokeWidth,
          type: 'line',
          x: pos.x,
          y: pos.y,
        };

        helpers.updateRuntimeStore({
          newElement: newElement as GenericElement,
          originalNewElement: { ...newElement } as GenericElement,
        });
        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
      }
    },
    onPointerMove: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;

      if (
        button === 'left' &&
        runtimeState.newElement &&
        runtimeState.newElement.type === 'line'
      ) {
        const element = runtimeState.newElement as LineElement;
        const originalElement = runtimeState.originalNewElement as LineElement;

        const getPointer = (): Point => {
          if (!runtimeState.activeKeyPress?.shift) return [pos.x, pos.y];

          const localRefPoint = originalElement.points[0];
          const globalRefPoint = [
            originalElement.x + localRefPoint[0],
            originalElement.y + localRefPoint[1],
          ] as Point;
          return snapToAngle(
            pos.x,
            pos.y,
            globalRefPoint[0],
            globalRefPoint[1],
            SNAPPING_ANGLE,
          );
        };

        const updated = translatePoint(
          1,
          element,
          originalElement,
          getPointer(),
          [element.originX, element.originY],
        );

        const newElement = { ...element, ...updated } as GenericElement;

        helpers.updateRuntimeStore({ newElement });
        helpers.renderNewElementScene();
      }
    },
    onPointerUp: ({ elementState, helpers, history, runtimeState }) => {
      if (!runtimeState.newElement || runtimeState.activeTool !== 'line')
        return;

      const lineElement = {
        ...runtimeState.newElement,
        shape: getLineShape(runtimeState.newElement as LineElement),
      };

      addElement(elementState.elements, lineElement);
      history.push(updateEditorStore({ selectedElementIds: [lineElement.id] }));
      updateRuntimeStore({
        activeTool: 'selection',
        newElement: undefined,
        originalNewElement: undefined,
      });

      helpers.renderStaticScene();
      helpers.hideNewElementCanvas();
    },
  },
  render: {
    padding: (element) => element.strokeWidth * 12,
    draw: ({
      config,
      context,
      element,
      runtimeState,
      elementState,
      skipElementUpdate,
    }) => {
      if (!runtimeState.newElement && !element.points.length) return;

      context.save();

      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = element.strokeWidth;
      context.strokeStyle = element.strokeColor;

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      const path = new Path2D();
      path.moveTo(element.points[0][0], element.points[0][1]);
      path.lineTo(element.points[1][0], element.points[1][1]);

      if (!skipElementUpdate) {
        updateElement<GenericPolyElement>(elementState.elements, element, {
          path,
        });
      }

      context.stroke(path);

      context.restore();
    },
    translate: (context, element, scale) => {
      const [x1, y1] = getLineBounds(element.points, element.x, element.y);

      const canvasOffsetX =
        element.x > x1 ? distance(element.x, x1) * scale : 0;
      const canvasOffsetY =
        element.y > y1 ? distance(element.y, y1) * scale : 0;

      context.translate(canvasOffsetX, canvasOffsetY);

      return { x: canvasOffsetX, y: canvasOffsetY };
    },
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: true,
    bounds: (element) => getLineBounds(element.points, element.x, element.y),
    calculateShape: (element) => getLineShape(element),
    isPointInShape: () => false,
    isPointOnShape: poly.collision.isPointOnShape,
  },
  transformation: {
    shiftBySizeOnFlip: false,
    getCursorFromTransformation: () => 'pointer',
    onSingleResize: (_, initial, nextWidth, nextHeight) => {
      const rescaledElementPointsY = rescalePoints(
        1,
        nextHeight,
        initial.points,
        true,
      );
      return {
        points: rescalePoints(0, nextWidth, rescaledElementPointsY, true) as [
          Point,
          Point,
        ],
      };
    },
    onMultiResize: (_, initial, nextWidth, nextHeight) => {
      const rescaledElementPointsY = rescalePoints(
        1,
        nextHeight,
        initial.points,
        false,
      );
      return {
        points: rescalePoints(0, nextWidth, rescaledElementPointsY, false) as [
          Point,
          Point,
        ],
      };
    },
    resizedBounds: (element, newWidth, newHeight) => {
      const resizedPoints = rescalePoints(
        0,
        newWidth,
        rescalePoints(1, newHeight, element.points, true),
        true,
      );
      return getLineBounds(
        resizedPoints as [Point, Point],
        element.x,
        element.y,
      );
    },
    resizeTestFromBounds: (element, [cursorX, cursorY], lib) => {
      const handles = getLineTransformHandles!(element, lib);

      const found = Object.keys(handles).find((key) => {
        return isInsideTransformHandle(handles[key], cursorX, cursorY);
      });

      return found ? found : null;
    },
    transformElement: (
      transform,
      element,
      original,
      [pointerX, pointerY],
      [originX, originY],
      { elementState, runtimeState },
    ) => {
      const originalElement = original.get(element.id) as LineElement;

      if (transform === 'move') {
        return moveMultipleElements(
          [element],
          [pointerX, pointerY],
          [originX, originY],
        );
      }

      const transformPointIndex = transform === 'first' ? 0 : 1;

      const getPointer = (): Point => {
        if (!runtimeState.activeKeyPress?.shift) return [pointerX, pointerY];

        const localRefPoint =
          originalElement.points[transformPointIndex === 0 ? 1 : 0];
        const refPoint = [
          originalElement.x + localRefPoint[0],
          originalElement.y + localRefPoint[1],
        ] as Point;
        return snapToAngle(
          pointerX,
          pointerY,
          refPoint[0],
          refPoint[1],
          SNAPPING_ANGLE,
        );
      };

      const updated = translatePoint(
        transformPointIndex,
        element,
        originalElement,
        getPointer(),
        [originX, originY],
      );

      updateElement<LineElement>(elementState.elements, element, updated);
    },
  },
  selection: {
    getTransformHandles: getLineTransformHandles,
    renderSelectionBorder: () => undefined,
    renderTransformHandles: (context, handles, { state, config }) => {
      context.save();

      context.lineWidth = 1.5 / state.zoom.value;
      context.strokeStyle = config.selection.strokeStyle;
      context.fillStyle = config.selection.transformFillStyle;

      Object.keys(handles).forEach((key) => {
        const handle = handles[key];
        const [x, y, width, height] = handle;
        fillCircle(context, x + width / 2, y + height / 2, width / 2);
      });

      context.restore();
    },
  },
  export: (element) => {
    const prepared = prepareElementForExport(element) as LineElement;

    prepared.points = [
      [
        roundExportVal(prepared.points[0][0]),
        roundExportVal(prepared.points[0][1]),
      ],
      [
        roundExportVal(prepared.points[1][0]),
        roundExportVal(prepared.points[1][1]),
      ],
    ];

    return prepared;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as LineElement;

    imported.shape = getLineShape(imported);

    return imported;
  },
};

export const line = plugin;
